import React from 'react';
import { Spinner, Badge } from 'react-bootstrap';
import { FaHandshake } from 'react-icons/fa';
import { useDetailSort } from '../hooks/useDetailSort';
import { ThSort, DetailPager, DetailSectionHeader } from '../shared/index';
import { detailSectionCard } from '../utils/constants';
import { fmt, fmtDate } from '../utils/helpers';
import { exportExcel, rowsToHtml, copyHtmlToClipboard } from '../utils/exports';

const cols = [
  { key: 'id_contrat',    label: 'Contrat' },
  { key: 'dateReglement', label: 'Date règlement' },
  { key: 'refReglement',  label: 'Référence' },
  { key: 'projet',        label: 'Projet' },
  { key: 'type',          label: 'Type' },
  { key: 'tranche',       label: 'Tranche' },
  { key: 'modeRegl',      label: 'Mode règl.' },
  { key: 'banque',        label: 'Banque' },
  { key: 'montant',       label: 'Montant', right: true },
  { key: 'statut',        label: 'Statut' },
  { key: 'commercial',    label: 'Commercial' },
];

const statutColor = (s) => {
  const u = (s || '').toUpperCase();
  if (['ENCAISSE','NOTAIRE'].includes(u))                                  return { bg: '#e8f5e9', color: '#2e7d32' };
  if (['PTF','EI','VERSEMENT','REPRESENTE','A_NE_PAS_VERSER_R'].includes(u)) return { bg: '#e3f2fd', color: '#1565c0' };
  if (u === 'RECEPTION')                                                    return { bg: '#e8f5e9', color: '#1b5e20' };
  return { bg: '#f5f5f5', color: '#666' };
};

const DetailTableRec = ({ data, loading, open, setOpen }) => {
  const { visible, sortKey, asc, handleSort, page, setPage, totalPages, total } = useDetailSort(data);

  return (
    <div style={detailSectionCard}>
      <DetailSectionHeader
        title="Détail Recouvrement" icon={<FaHandshake size={13} />}
        color="#a5d6a7" count={data.length} loading={loading} open={open} setOpen={setOpen}
        onExport={() => exportExcel(data.map(r => ({
          'ID Contrat': r.id_contrat || '', 'Date Règlement': r.dateReglement,
          'Référence': r.refReglement, 'Projet': r.projet, 'Type': r.type,
          'Tranche': r.tranche, 'Mode Règl.': r.modeRegl, 'Banque': r.banque,
          'Montant': r.montant, 'Statut': r.statut, 'Commercial': r.commercial,
        })), 'detail-recouvrement')}
        onCopy={() => { const { html, plain } = rowsToHtml(data.map(r => ({
          'Contrat': r.id_contrat || '', 'Date': fmtDate(r.dateReglement), 'Réf': r.refReglement || '',
          'Projet': r.projet, 'Type': r.type || '', 'Tranche': r.tranche || '',
          'Mode': r.modeRegl || '', 'Banque': r.banque || '',
          'Montant': fmt(r.montant), 'Statut': r.statut || '', 'Commercial': r.commercial || '',
        })), 'green'); copyHtmlToClipboard(html, plain); }}
      />
      {open && (
        <>
          {loading ? (
            <div style={{ padding: 30, textAlign: 'center' }}><Spinner /></div>
          ) : data.length === 0 ? (
            <div style={{ padding: 30, textAlign: 'center', color: '#aaa', fontSize: '0.85rem' }}>Aucun encaissement sur la période</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                <thead><tr>{cols.map(c => <ThSort key={c.key} label={c.label} field={c.key} sortKey={sortKey} asc={asc} onSort={handleSort} right={c.right} />)}</tr></thead>
                <tbody>
                  {visible.map((row, i) => {
                    const sc = statutColor(row.statut);
                    return (
                      <tr key={`${row.id}-${i}`} style={{ borderBottom: '1px solid #f0ebe4', background: i % 2 === 0 ? '#fff' : '#fdfaf7' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f9f3ed'}
                        onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? '#fff' : '#fdfaf7'}>
                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>
                          {row.id_contrat
                            ? <a href={`https://shems.ngrok.io/consultation/${row.id_contrat}`} target="_blank" rel="noreferrer"
                                style={{ color: '#6d4e36', fontWeight: 700, textDecoration: 'none', borderBottom: '1px dashed #c8a882' }}>#{row.id_contrat}</a>
                            : '—'}
                        </td>
                        <td style={{ padding: '5px 8px', whiteSpace: 'nowrap' }}>{fmtDate(row.dateReglement)}</td>
                        <td style={{ padding: '5px 8px', color: '#6d4e36', fontWeight: 600 }}>{row.refReglement || '—'}</td>
                        <td style={{ padding: '5px 8px', fontWeight: 500 }}>{row.projet}</td>
                        <td style={{ padding: '5px 8px' }}><Badge bg="none" style={{ background: '#f9f5f1', color: '#6d4e36', border: '1px solid #e8ddd4', fontSize: '0.65rem' }}>{row.type || '—'}</Badge></td>
                        <td style={{ padding: '5px 8px', color: '#666' }}>{row.tranche || '—'}</td>
                        <td style={{ padding: '5px 8px' }}>{row.modeRegl || '—'}</td>
                        <td style={{ padding: '5px 8px', color: '#555' }}>{row.banque || '—'}</td>
                        <td style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 700, color: '#2e7d32' }}>{fmt(row.montant)}</td>
                        <td style={{ padding: '5px 8px' }}>
                          <Badge bg="none" style={{ background: sc.bg, color: sc.color, border: `1px solid ${sc.color}30`, fontSize: '0.65rem' }}>{row.statut}</Badge>
                        </td>
                        <td style={{ padding: '5px 8px', color: '#555' }}>{row.commercial || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <DetailPager page={page} setPage={setPage} totalPages={totalPages} total={total} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DetailTableRec;
